import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { toast } from "react-toastify";
import { getExpenseReportApi } from "../services/expenseReport.service";
import { exportExpenseReportApi, exportExpenseReportPDF } from "../services/reports.service";
import { getUserApi } from "../services/user.service";
import { useGetApi } from "../hooks/useGetApi";
import { DEFAULT_LIMIT, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)", // Background color
  width: "44px", // Width
  height: "44px", // Height
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};
const headLabel = [
  {
    id: 1,
    label: "UHID",
    value: "uhid",
  },

  {
    id: 2,
    label: "Patient Name ",
    value: "patientName",
  },

  {
    id: 3,
    label: "Reg Date",
    value: "regDate",
  },
  {
    id: 4,
    label: "Discharge Date",
    value: "dischargeDate",
  },
  {
    id: 5,
    label: "Consulation Charges",
    value: "consulatationCharges",
  },
  {
    id: 6,
    label: "Procedure Charges",
    value: "procedureCharges",
  },
];

// Proper dropdown options
const templateOptions = [
  { label: "All IPD Expenses", value: "all_ipd_expenses" },
  { label: "Consultation Expenses", value: "consultation_expenses" },
  { label: "Procedure Expenses", value: "procedure_expenses" },
  { label: "Room Charges", value: "room_charges" },
  { label: "Medicine Expenses", value: "medicine_expenses" },
  { label: "Lab Expenses", value: "lab_expenses" },
  { label: "Diagnostic Expenses", value: "diagnostic_expenses" },
];

const schemeOptions = [
  { label: "General", value: "general" },
  { label: "CGHS", value: "cghs" },
  { label: "ECHS", value: "echs" },
  { label: "Insurance", value: "insurance" },
  { label: "Corporate", value: "corporate" },
  { label: "Cash", value: "cash" },
];

const categoryOptions = [
  { label: "OPD", value: "opd" },
  { label: "IPD", value: "ipd" },
  { label: "Emergency", value: "emergency" },
  { label: "Pharmacy", value: "pharmacy" },
  { label: "Laboratory", value: "laboratory" },
  { label: "Radiology", value: "radiology" },
];

const subCategoryOptions = [
  { label: "Consultation", value: "consultation" },
  { label: "Procedure", value: "procedure" },
  { label: "Investigation", value: "investigation" },
  { label: "Medicine", value: "medicine" },
  { label: "Room Rent", value: "room_rent" },
  { label: "Nursing Care", value: "nursing_care" },
];

const unitOptions = [
  { label: "General Ward", value: "general_ward" },
  { label: "ICU", value: "icu" },
  { label: "CCU", value: "ccu" },
  { label: "NICU", value: "nicu" },
  { label: "Private Room", value: "private_room" },
  { label: "Semi Private", value: "semi_private" },
];

// Doctor options will be fetched from API

const emcsOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Emergency", value: "emergency" },
  { label: "Regular", value: "regular" },
];

const appropriateOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
  { label: "Pending", value: "pending" },
  { label: "Under Review", value: "under_review" },
];

export default function ReportsExpenseDetailStatement() {
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(-1);
  };
  const [formData, setFormData] = useState({
    searchTemplate: null,
    startDate: null,
    endDate: null,
    schemes: null,
    category: null,
    subCategory: null,
    unit: null,
    doctor: null,
    emcs: null,
    appropriate: null,
  });

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors
  
  // Fetch doctors for dropdown
  const doctorApiResult = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000 },
  });

  // Extract doctors data and loading state
  const doctorsData = doctorApiResult.dataList;
  const doctorsLoading = doctorApiResult.isLoading;

  // Process doctors data to filter for actual doctors
  const doctorOptions = useMemo(() => {
    if (!doctorsData || !Array.isArray(doctorsData)) return [];
    return doctorsData
      .filter(user => {
        // More flexible doctor filtering - check multiple possible role indicators
        const role = user.role;
        if (!role) {
          // Check if name contains "Dr" for users without role
          const nameContainsDoctor = (user.firstName + ' ' + user.lastName).toLowerCase().includes('dr');
          return nameContainsDoctor;
        }
        
        // Check the correct role field - it's role.role, not role.roleName!
        const roleName = role.role || role.roleName || role.title || role.name || role.type || '';
        const roleNameLower = roleName.toLowerCase();
        
        // Check if it contains doctor-related keywords
        const isDoctorRole = roleNameLower.includes('doctor') || 
                           roleNameLower.includes('physician') || 
                           roleNameLower.includes('dr') ||
                           roleNameLower === 'doctor' ||
                           user.isDoctor === true ||
                           user.userType === 'doctor';
        
        return isDoctorRole;
      })
      .map(doctor => ({
        label: `${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() || doctor.email || 'Unknown Doctor',
        value: doctor._id
      }));
  }, [doctorsData]);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    uhid: true,
    patientName: true,
    regDate: true,
    dischargeDate: true,
    consulatationCharges: true,
    procedureCharges: true,
  });

  // Menu anchor states
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch report data - accepts optional parameters to avoid stale closure issues
  const fetchReportData = async (currentPage = page, currentRowsPerPage = rowsPerPage, showToast = false) => {
    try {
      setIsLoading(true);
      
      // Prepare params for API call
      let startDateFormatted, endDateFormatted;
      
      try {
        // Validate and format start date
        if (formData.startDate) {
          const startDate = formData.startDate instanceof Date ? formData.startDate : new Date(formData.startDate);
          if (!isNaN(startDate.getTime())) {
            startDateFormatted = format(startDate, 'yyyy-MM-dd');
          } else {
            throw new Error('Invalid start date');
          }
        } else {
          startDateFormatted = null;
        }
        
        // Validate and format end date
        if (formData.endDate) {
          const endDate = formData.endDate instanceof Date ? formData.endDate : new Date(formData.endDate);
          if (!isNaN(endDate.getTime())) {
            endDateFormatted = format(endDate, 'yyyy-MM-dd');
          } else {
            throw new Error('Invalid end date');
          }
        } else {
          endDateFormatted = null;
        }
      } catch (dateError) {
        console.error("Date formatting error:", dateError);
        toast.error('Error formatting dates. Please check your date selections.');
        setIsLoading(false);
        return;
      }

      const params = {
        ...formData,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        // Note: visitType filter might be too restrictive - try without it first
        // visitType: 'IPD', // Filter for IPD only
        page: currentPage + 1, // API is 1-based, MUI is 0-based
        limit: currentRowsPerPage,
      };

      // Remove null/undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      // Extract values from dropdown objects
      const apiParams = {};
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value && typeof value === 'object' && value.value !== undefined) {
          apiParams[key] = value.value;
        } else {
          apiParams[key] = value;
        }
      });

      console.log('Fetching IPD Expense Detail Statement with params:', apiParams);
      const response = await getExpenseReportApi(apiParams);
      console.log('API Response:', response);
      
      // Handle response following standard pattern
      if (response && (response.isSuccess || response.data)) {
        const responseData = response.data || response || {};
        let dataArray = [];
        
        // Try different possible response structures
        if (responseData.data && Array.isArray(responseData.data)) {
          dataArray = responseData.data;
        } else if (Array.isArray(responseData)) {
          dataArray = responseData;
        } else if (responseData.items && Array.isArray(responseData.items)) {
          dataArray = responseData.items;
        } else if (responseData.records && Array.isArray(responseData.records)) {
          dataArray = responseData.records;
        }
        
        console.log('Data array extracted:', dataArray);
        
        // Transform API response data to match table structure
        const transformedData = dataArray.map((item, index) => {
          // Helper function to safely format dates
          const formatDate = (dateValue) => {
            if (!dateValue) return '-';
            try {
              const date = new Date(dateValue);
              if (isNaN(date.getTime())) return '-';
              return date.toLocaleDateString('en-GB');
            } catch (error) {
              return '-';
            }
          };

          return {
            _id: item._id || `item-${index}`,
            uhid: item.uhid || item.patientUHID || item.UHID || '-',
            patientName: item.patientName || 
                        `${item.firstName || ''} ${item.lastName || ''}`.trim() || 
                        item.patient?.name || '-',
            regDate: formatDate(item.regDate || item.admissionDate || item.visitDate || item.consultationDateTime),
            dischargeDate: formatDate(item.dischargeDate) || '-',
            consulatationCharges: item.consulatationCharges || item.consultationCharges || item.amount || '0',
            procedureCharges: item.procedureCharges || '0'
          };
        });
        
        console.log('Transformed data:', transformedData);
        
        setTableData(transformedData);
        setTotalCount(responseData.pagination?.total || responseData.total || dataArray.length || 0);
        
        if (showToast) {
          if (transformedData.length === 0) {
            toast.info('No data found for the selected criteria');
          } else {
            toast.success("Report data loaded successfully");
          }
        }
      } else {
        const errorMessage = response?.message || 'Failed to fetch expense report';
        console.error('API Error:', errorMessage, response);
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error fetching expense report:', err);
      const errorMessage = err.message || 'An error occurred while fetching the report';
      toast.error(errorMessage);
      setTableData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.startDate) {
      errorFields.push('startDate');
    }
    if (!formData.endDate) {
      errorFields.push('endDate');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    isSearchingRef.current = true;
    setPage(0); // Reset to first page on new search
    setHasSearched(true);
    fetchReportData(0, rowsPerPage, true);
  };

  // Handle filter icon click (column visibility)
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'IPD Patient Expense Detail Statement',
        text: `IPD Patient Expense Detail Statement - ${totalCount || 0} records`,
        url: window.location.href,
      }).catch((error) => {
        console.log('Error sharing:', error);
      });
    } else {
      // Fallback: Copy URL to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        toast.success('Report URL copied to clipboard');
      }).catch(() => {
        toast.error('Failed to copy URL');
      });
    }
  };

  // Handle download menu
  const handleDownloadClick = (event) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  // Handle menu item clicks
  const handleMenuClick = (item) => {
    handleDownloadClose();
    switch (item) {
      case 'Excel':
        handleExport();
        break;
      case 'PDF':
        handlePDFExport();
        break;
      case 'Report Mail Send Scheduler':
        toast.info('Report Mail Send Scheduler feature coming soon');
        break;
      case 'Report Scheduler':
        toast.info('Report Scheduler feature coming soon');
        break;
      default:
        break;
    }
  };

  // Handle export functionality
  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Prepare params for export - matching fetchReportData params
      let startDateFormatted, endDateFormatted;
      
      // Validate and format start date
      if (formData.startDate) {
        const startDate = formData.startDate instanceof Date ? formData.startDate : new Date(formData.startDate);
        if (!isNaN(startDate.getTime())) {
          startDateFormatted = format(startDate, 'yyyy-MM-dd');
        } else {
          throw new Error('Invalid start date');
        }
      } else {
        startDateFormatted = null;
      }
      
      // Validate and format end date
      if (formData.endDate) {
        const endDate = formData.endDate instanceof Date ? formData.endDate : new Date(formData.endDate);
        if (!isNaN(endDate.getTime())) {
          endDateFormatted = format(endDate, 'yyyy-MM-dd');
        } else {
          throw new Error('Invalid end date');
        }
      } else {
        endDateFormatted = null;
      }
      
      const exportParams = {
        ...formData,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        // Note: visitType filter might be too restrictive - try without it first
        // visitType: 'IPD', // Filter for IPD only
        page: 1,
        limit: 100000, // Export all records
      };

      // Remove null/undefined values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === null || exportParams[key] === undefined || exportParams[key] === '') {
          delete exportParams[key];
        }
      });

      // Extract values from dropdown objects
      const apiParams = {};
      Object.keys(exportParams).forEach(key => {
        const value = exportParams[key];
        if (value && typeof value === 'object' && value.value !== undefined) {
          apiParams[key] = value.value;
        } else {
          apiParams[key] = value;
        }
      });

      // Define column order for Excel export (matching headLabel order)
      const columnOrder = headLabel.filter(col => visibleColumns[col.value]).map(col => col.value);

      const result = await exportExpenseReportApi(apiParams, columnOrder);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (err) {
      console.error('Export error:', err);
      const errorMessage = err.message || 'Failed to export report';
      toast.error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle PDF export
  const handlePDFExport = async () => {
    try {
      setIsExporting(true);
      
      // Prepare export parameters - matching fetchReportData params
      let startDateFormatted, endDateFormatted;
      
      // Validate and format start date
      if (formData.startDate) {
        const startDate = formData.startDate instanceof Date ? formData.startDate : new Date(formData.startDate);
        if (!isNaN(startDate.getTime())) {
          startDateFormatted = format(startDate, 'yyyy-MM-dd');
        } else {
          throw new Error('Invalid start date');
        }
      } else {
        startDateFormatted = null;
      }
      
      // Validate and format end date
      if (formData.endDate) {
        const endDate = formData.endDate instanceof Date ? formData.endDate : new Date(formData.endDate);
        if (!isNaN(endDate.getTime())) {
          endDateFormatted = format(endDate, 'yyyy-MM-dd');
        } else {
          throw new Error('Invalid end date');
        }
      } else {
        endDateFormatted = null;
      }
      
      const exportParams = {
        ...formData,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        // Note: visitType filter might be too restrictive - try without it first
        // visitType: 'IPD', // Filter for IPD only
        page: 1,
        limit: 100000, // Export all records for PDF
      };

      // Remove null/undefined values
      Object.keys(exportParams).forEach(key => {
        if (exportParams[key] === null || exportParams[key] === undefined || exportParams[key] === '') {
          delete exportParams[key];
        }
      });

      // Extract values from dropdown objects
      const apiParams = {};
      Object.keys(exportParams).forEach(key => {
        const value = exportParams[key];
        if (value && typeof value === 'object' && value.value !== undefined) {
          apiParams[key] = value.value;
        } else {
          apiParams[key] = value;
        }
      });

      // Define columns for PDF export with proper labels (matching headLabel order and visible columns)
      const allColumns = headLabel.map(col => ({
        header: col.label,
        dataKey: col.value,
        visibleKey: col.value
      }));
      
      // Filter columns based on visibleColumns state
      const columns = allColumns.filter(col => visibleColumns[col.visibleKey]).map(({ visibleKey, ...col }) => col);

      // Prepare date range for PDF header
      const dateRange = formData.startDate && formData.endDate ? {
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
      } : null;

      const result = await exportExpenseReportPDF(apiParams, columns, dateRange, 'IPD Patient Expense Detail Statement');
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to PDF!`);
      } else {
        throw new Error('PDF export failed');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(`PDF export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle pagination
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Refetch data when pagination changes (only if user has already searched)
  const prevPageRef = useRef(page);
  const prevRowsPerPageRef = useRef(rowsPerPage);
  const isSearchingRef = useRef(false);
  
  useEffect(() => {
    // Skip if no search has been performed yet
    if (!hasSearched) {
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return;
    }
    
    // Skip if we're currently performing a search (handleSearch is running)
    if (isSearchingRef.current) {
      isSearchingRef.current = false;
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
      return; // handleSearch already fetched the data
    }
    
    // Check if page or rowsPerPage actually changed
    const pageChanged = prevPageRef.current !== page;
    const rowsPerPageChanged = prevRowsPerPageRef.current !== rowsPerPage;
    
    // Only refetch if:
    // 1. User has performed a search (hasSearched flag)
    // 2. Page or rowsPerPage actually changed (not just a re-render)
    if (hasSearched && (pageChanged || rowsPerPageChanged)) {
      fetchReportData(page, rowsPerPage);
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    } else {
      // Update refs even if we don't fetch (to track current state)
      prevPageRef.current = page;
      prevRowsPerPageRef.current = rowsPerPage;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              IPD Patient Expense Detail Statement
            </Typography>
          </Box>
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => handleInputChange('searchTemplate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="startDate"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(newValue) => {
                        handleInputChange('startDate', newValue);
                        // Clear error when field is changed
                        if (errors.includes('startDate')) {
                          setErrors(errors.filter(e => e !== 'startDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('startDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      name="endDate"
                      label="End Date"
                      value={formData.endDate}
                      onChange={(newValue) => {
                        handleInputChange('endDate', newValue);
                        // Clear error when field is changed
                        if (errors.includes('endDate')) {
                          setErrors(errors.filter(e => e !== 'endDate'));
                        }
                      }}
                      slotProps={{
                        textField: { 
                          required: true, 
                          fullWidth: true,
                          InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                        },
                      }}
                      sx={{ 
                        bgcolor: "white",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('endDate') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={schemeOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.schemes}
                  onChange={(event, newValue) => handleInputChange('schemes', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Schemes"
                      name="schemes"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={categoryOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.category}
                  onChange={(event, newValue) => handleInputChange('category', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      name="category"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={subCategoryOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.subCategory}
                  onChange={(event, newValue) => handleInputChange('subCategory', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sub Category"
                      name="subCategory"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={unitOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.unit}
                  onChange={(event, newValue) => handleInputChange('unit', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Unit" name="unit" fullWidth />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                    options={doctorOptions || []}
                    getOptionLabel={(option) => option?.label || ""}
                    value={formData.doctor}
                    onChange={(event, newValue) => handleInputChange('doctor', newValue)}
                    loading={doctorsLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Doctor"
                        name="doctor"
                        fullWidth
                      />
                    )}
                    sx={{ bgcolor: "white" }}
                  />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={emcsOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.emcs}
                  onChange={(event, newValue) => handleInputChange('emcs', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="EMCS" name="emcs" fullWidth />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={appropriateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.appropriate}
                  onChange={(event, newValue) => handleInputChange('appropriate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Appropriate"
                      name="appropriate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting || tableData.length === 0}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              my: "20px",
              p: "20px",
            }}
          >
              {/* Header with Rows/Columns count and action icons */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pb: "15px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                  >
                    Rows: {Array.isArray(tableData) ? tableData.length : 0} | Columns:{" "}
                    {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon
                        sx={{
                          cursor: "pointer",
                          color: "transparent",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleShare}>
                      <ShareIcon 
                        sx={{
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps}>
                      <IconButton onClick={handleDownloadClick}>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <TableContainer component={Paper}>
                <Table aria-label="expense detail statement table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {headLabel?.map((option) => 
                        visibleColumns[option.value] && (
                          <TableCell
                            key={option?.id}
                            sx={{ textAlign: option?.align || "left" }}
                          >
                            {option?.label}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !Array.isArray(tableData) || tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData?.map((row) => (
                        <TableRow key={row?._id}>
                          {headLabel?.map((column) => 
                            visibleColumns[column.value] && (
                              <TableCell key={column?.id}>
                                {row?.[column?.value]}
                              </TableCell>
                            )
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {totalCount > 0 && (
                <TablePagination
                  rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                  component="div"
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              )}
              
              {/* Filter Menu */}
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={handleFilterClose}
                PaperProps={{
                  style: {
                    maxHeight: 400,
                    padding: "8px 0",
                    width: "200px",
                  },
                }}
              >
                <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                  Show/Hide Columns
                </MenuItem>
                <Divider />
                {headLabel.map((column) => (
                  <MenuItem
                    key={column.id}
                    onClick={() => toggleColumn(column.value)}
                    sx={menuItemStyle}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ flex: 1 }}>{column.label}</Box>
                      {visibleColumns[column.value] && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
              
              {/* Download Menu */}
              <Menu
                anchorEl={downloadAnchorEl}
                open={downloadMenuOpen}
                onClose={handleDownloadClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "250px",
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <React.Fragment key={item}>
                    <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                      {item}
                    </MenuItem>
                    {index !== menuItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Menu>
            </Box>
        </Box>
      </Box>
    </Container>
  );
}
